import { authApiClient } from '../auth/AuthApiClient';

const PRESENTER_API_BASE = (
  import.meta.env.VITE_PRESENTER_API_BASE || '/presenter-api'
).replace(/\/$/, '');

export interface CloudPresentationSummary {
  id: number;
  title: string;
  description?: string | null;
  is_public: boolean;
  share_url?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CloudPresentationDetail extends CloudPresentationSummary {
  content: string;
}

export interface CloudPresentationWrite {
  title: string;
  description?: string | null;
  content: string;
}

/** #1762: an owner-authorized observation of what an anonymous visitor gets, not a guarantee. */
export interface PresentationAvailability {
  is_public: boolean;
  anonymous: 'available' | 'review_pending' | 'unavailable' | 'not_public';
  checked_at: string;
}

export interface MediaUploadResult {
  id: number;
  uri: string;
  mime_type: string;
  original_filename: string;
}

/** Draft endpoint (contract p-b8b361e72d75, Post #4963). */
export type DraftMode = 'media' | 'prompt' | 'catalog';

export interface DraftCatalogNode {
  id: string;
  parent_id: string | null;
  title: string;
  body?: string;
  media_id?: number | null;
  source_url?: string | null;
}

export interface DraftRequest {
  version: 1;
  mode: DraftMode;
  title?: string;
  language?: 'de' | 'en';
  prompt?: string;
  media_ids?: number[];
  max_steps?: number;
  catalog?: { source: string; nodes: DraftCatalogNode[] };
  /** The current ---GLOBAL--- header, kept byte-for-byte in front of the generated story. */
  global_header?: string;
}

export interface DraftResponse {
  version: 1;
  mode: DraftMode;
  title: string;
  content: string;
  step_count: number;
  sources: { step_id: string; catalog_node_ids?: string[]; media_ids?: number[] }[];
  selection: { input_nodes: number; represented_nodes: number; omitted_nodes: number };
  warnings: { code: string; message: string }[];
}

/** Error with the HTTP status and the contract's `detail.code`, so the UI can word it. */
export class PresenterApiError extends Error {
  constructor(message: string, public readonly status: number, public readonly code: string | null) {
    super(message);
  }
}

async function readResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let detail = `${response.status} ${response.statusText}`;
    let code: string | null = null;
    try {
      const payload = await response.json();
      code = typeof payload.detail?.code === 'string' ? payload.detail.code : null;
      detail = payload.detail?.message || payload.detail || payload.error || detail;
    } catch {
      // Preserve the HTTP fallback.
    }
    throw new PresenterApiError(String(detail), response.status, code);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export class PresentationApiClient {
  async getPublic(id: string, signal?: AbortSignal): Promise<CloudPresentationDetail> {
    if (!/^[1-9]\d*$/.test(id) || !Number.isSafeInteger(Number(id))) {
      throw new PublicPresentationError(404);
    }
    const response = await fetch(`${PRESENTER_API_BASE}/api/v1/public/presentations/${id}`, {
      signal, credentials: 'omit', cache: 'no-store',
    });
    if (!response.ok) {
      // BFF contract (#1761): detail.code names the state; anything else stays generic.
      let code: string | null = null;
      try {
        const payload = await response.json();
        code = typeof payload?.detail?.code === 'string' ? payload.detail.code : null;
      } catch {
        // no JSON body: keep the status only
      }
      throw new PublicPresentationError(response.status, code);
    }
    return response.json() as Promise<CloudPresentationDetail>;
  }

  async list(): Promise<CloudPresentationSummary[]> {
    return this.authorized('/api/v1/presentations');
  }

  async get(id: number): Promise<CloudPresentationDetail> {
    return this.authorized(`/api/v1/presentations/${id}`);
  }

  async create(input: CloudPresentationWrite): Promise<CloudPresentationDetail> {
    return this.authorized('/api/v1/presentations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
  }

  async update(id: number, input: CloudPresentationWrite): Promise<CloudPresentationDetail> {
    return this.authorized(`/api/v1/presentations/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
  }

  async remove(id: number): Promise<void> {
    return this.authorized(`/api/v1/presentations/${id}`, { method: 'DELETE' });
  }

  async setPublication(id: number, isPublic: boolean): Promise<CloudPresentationSummary> {
    return this.authorized(`/api/v1/presentations/${id}/publication`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_public: isPublic }),
    });
  }

  /** #1762: one observation per call; the caller decides when, never on a timer. */
  async getAvailability(id: number): Promise<PresentationAvailability> {
    return this.authorized(`/api/v1/presentations/${id}/availability`, { cache: 'no-store' });
  }

  /** Generate an editable draft; never saves or publishes anything. */
  async createDraft(request: DraftRequest): Promise<DraftResponse> {
    return this.authorized('/api/v1/presentations/draft', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
  }

  async uploadMedia(file: File): Promise<MediaUploadResult> {
    const form = new FormData();
    form.set('file', file, file.name);
    return this.authorized('/api/v1/media', { method: 'POST', body: form });
  }

  private async authorized<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = authApiClient.accessToken;
    if (!token) throw new PresenterApiError('Anmeldung erforderlich', 401, 'AUTH_REQUIRED');
    const headers = new Headers(init.headers);
    headers.set('Authorization', `Bearer ${token}`);
    return readResponse<T>(
      await fetch(`${PRESENTER_API_BASE}${path}`, { ...init, headers }),
    );
  }
}

export const presentationApiClient = new PresentationApiClient();

export class PublicPresentationError extends Error {
  constructor(public readonly status: number, public readonly code: string | null = null) {
    super(status === 404 ? 'Präsentation nicht gefunden' : 'Präsentation konnte nicht geladen werden');
  }
}
